import { AsyncPipe, NgClass, NgForOf, NgIf, NgStyle } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { LocationType } from 'app/models';
import { AppDataService, MerchantService, StoreService } from 'app/services';
import { MessageService } from 'primeng/api';
import { Observable, startWith, switchMap } from 'rxjs';
import { StoreDto } from '../interfaces/store-dto.interface';
import { MerchantStoreItem } from '../types/merchant-store-item.type';
import { duplicateAddressValidator } from '../validators/duplicate-address.validator';
import { fileUploadValidator } from 'app/components/merchant/partners-portal/validators/file-upload.validator';
import { ComboItemDto } from 'app/merchant/partners-portal/interfaces/combo-item.interface';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';

@Component({
  selector: 'create-store-dialog-form',
    imports: [
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressBarModule,
        MatSelectModule,
        MatChipsModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        MatRadioButton,
        MatRadioGroup,
        NgIf,
        NgForOf,
        NgClass,
        AsyncPipe,
        NgStyle,
    ],
  standalone: true,
  templateUrl: './create-store-dialog-form.component.html',
  styleUrls: ['./create-store-dialog-form.component.scss'],
  providers: [MessageService],
})
export class CreateStoreDialogFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly storeService = inject(StoreService);
  private readonly messageService = inject(MessageService);
  private readonly merchantService = inject(MerchantService);
  private readonly appData = inject(AppDataService);
  private readonly data = inject<{
    store: MerchantStoreItem;
    stores: MerchantStoreItem[];
  }>(MAT_DIALOG_DATA);

  readonly locationTypeList = LocationType.LocationTypeList;
  merchantsList: ComboItemDto[] = [];

  editedStore!: MerchantStoreItem;
  photoFilename: string = '';
  isLoading = false;
  lat: number = 0;
  long: number = 0;

  // ? Google Maps API variables
  @ViewChild('mapContainer') mapContainer!: ElementRef;
  filteredOptions$?: Observable<google.maps.places.AutocompletePrediction[]>;
  private map: google.maps.Map | undefined;
  private circle: google.maps.Circle | undefined;
  private autocompleteService:
    | google.maps.places.AutocompleteService
    | undefined;
  private placesService: google.maps.places.PlacesService | undefined;
  private geocoder?: google.maps.Geocoder;
  private currentMarker: google.maps.Marker | null = null;

  createStoreForm = this.fb.group({
    Address: this.fb.nonNullable.control('', Validators.required),
    StoreType: this.fb.nonNullable.control(1, Validators.required),
    ServiceableAreaRadius: this.fb.nonNullable.control(5, Validators.required),
    Radius: this.fb.nonNullable.control(5, Validators.required),
    Name: this.fb.nonNullable.control('', Validators.required),
    LocationTypeId: this.fb.nonNullable.control<number | null>(null, [
      Validators.required,
    ]),
    MerchantId: this.fb.nonNullable.control<number | null>(null, [
      Validators.required,
    ]),
    Brand: this.fb.nonNullable.control('', Validators.required),
    AddressDetails: this.fb.nonNullable.control('', Validators.required),
    Email: this.fb.nonNullable.control('', [Validators.required, Validators.email]),
    Phone: this.fb.nonNullable.control('', [Validators.required, Validators.pattern(/^(09|\+639)\d{9}$/)]),
    City: this.fb.nonNullable.control(''),
    PostalCode: this.fb.nonNullable.control(''),
    ImageUrl: this.fb.control<string | null>(null, [Validators.required]),
    locationPhotoFile: this.fb.control<File | null>(null, [
      Validators.required,
      fileUploadValidator(),
    ]),
  });

  @HostListener('dragover', ['$event']) onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('dragleave', ['$event']) onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('drop', ['$event']) onFileDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.onFileSelected(event);
  }

  ngOnInit(): void {
    this.setInitialFormValues();
    this.setFilename();
    this.createStoreForm.get('Address')?.valueChanges.subscribe((value: any) => {
      if (!value?.place_id) {
        this.createStoreForm.get('Address')?.setErrors({ invalidAddress: true });
      }
    });

    this.createStoreForm.get('Radius')?.valueChanges.subscribe(value => {
      this.updateRadius(value);
    });

    this.setMaps();
    this.getMerchantsForCombo();
  }

  getMerchantsForCombo(): void {
    const dto = {
      IsLimitedByCurrentUser: true,
    };
    this.merchantService.getMerchantsForCombo(dto, this.messageService)
        .then(data => {
          if (data && data.length > 0) {
            this.merchantsList = data;
          }
        })
        .catch(err => {
          throw err;
        });
  }

  private setInitialFormValues(): void {
    if (!this.data?.store) {
      return;
    }

    this.editedStore = this.data.store;
    this.createStoreForm.patchValue({
      ...this.editedStore,
      Address: this.editedStore.Address,
    });
    this.togglePhotoFileValidator();
  }

  private togglePhotoFileValidator(toggle: boolean = true): void {
    if (toggle) {
      return this.locationPhotoFile?.clearValidators();
    }

    return this.locationPhotoFile?.addValidators([
      Validators.required,
      fileUploadValidator(),
    ]);
  }

  private setFilename(): void {
    if (!this.editedStore) {
      return;
    }

    this.photoFilename = this.editedStore.ImageUrl;
    this.createStoreForm.get('ImageUrl')?.setValue(this.editedStore.ImageUrl);
  }

  onFileSelected(event: Event | DragEvent): void {
    const target = event.target as HTMLInputElement;
    let files = target?.files;

    const dataTransfer = (event as DragEvent).dataTransfer;
    if (dataTransfer) {
      files = dataTransfer.files;
    }

    if (!files || !files.length) {
      return;
    }

    const file = files[0];
    this.photoFilename = file.name;

    if (!file) {
      return;
    }

    this.locationPhotoFile?.setValue(file);
    this.updateLocationPhoto();
  }

  private updateLocationPhoto(): void {
    const { value } = this.createStoreForm;

    if (!value.locationPhotoFile) {
      return;
    }

    this.getBlobStringFromFile(value.locationPhotoFile);
  }

  private getBlobStringFromFile(file: File): void {
    const reader = new FileReader();

    reader.onload = (progressEvent: ProgressEvent<FileReader>) => {
      const fileReader = progressEvent.target as FileReader;
      this.createStoreForm
        .get('ImageUrl')
        ?.setValue(fileReader.result as string);
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
    };

    reader.readAsDataURL(file);
  }

  removeLocationPhoto(): void {
    this.locationPhotoFile?.reset();
    this.createStoreForm.get('ImageUrl')?.reset();
    this.photoFilename = '';
    this.togglePhotoFileValidator(false);
  }

  get locationPhotoFile(): AbstractControl<File | null, File | null> | null {
    return this.createStoreForm.get('locationPhotoFile');
  }

  onSubmit(): void {
    if (this.createStoreForm.invalid) {
      return;
    }

    if (this.editedStore) {
      return this.editStore();
    }

    this.createStore();
  }

  private editStore(): void {
    const { value } = this.createStoreForm;

    if (!value.ImageUrl) {
      return;
    }

    this.isLoading = true;
    const store: StoreDto = {
      Name: value.Name || '',
      MerchantId: this.merchantId,
      AddressDetails: value.AddressDetails || '',
      Lat: this.lat === 0 ? this.editedStore.Lat : this.lat,
      Long: this.long === 0 ? this.editedStore.Long : this.long,
      Brand: value.Brand || '',
      Email: value.Email || '',
      Phone: value.Phone || '',
      City: value.City || '',
      PostalCode: value.PostalCode || '',
      ServiceableAreaRadius: value.Radius || 0,
      Address: typeof value.Address === 'string'
          ? value.Address
          : (value.Address as any).description,
      LocationTypeId: value.LocationTypeId || 0,
      Radius: value.Radius || 0,
      StoreType: value.StoreType || 0,
      ImageUrl: value.ImageUrl,
      Id: this.editedStore.Id,
    };

    this.storeService
      .editStore(store, this.messageService)
      .then((data) => {
        this.dialogRef.close({
          Name: value.Name,
          AddressDetails: value.AddressDetails,
          MerchantId: this.merchantId,
          Lat: this.lat === 0 ? this.editedStore.Lat : this.lat,
          Long: this.long === 0 ? this.editedStore.Long : this.long,
          Brand: value.Brand || '',
          Email: value.Email,
          Phone: value.Phone,
          City: value.City || '',
          PostalCode: value.PostalCode || '',
          Address: value.Address,
          Radius: value.Radius || 0,
          ServiceableAreaRadius: value.Radius || 0,
          LocationTypeId: value.LocationTypeId,
          ImageUrl: data.ImageUrl,
          Id: this.editedStore.Id,
        });
      })
      .catch((err) => {
        this.dialogRef.close();
        throw err;
      })
      .finally(() => (this.isLoading = false));
  }

  private createStore(): void {
    const { value } = this.createStoreForm;

    if (!value.ImageUrl || !value.locationPhotoFile) {
      return;
    }

    this.isLoading = true;
    const store = {
      Name: value.Name || '',
      MerchantId: this.merchantId,
      AddressDetails: value.AddressDetails || '',
      Lat: this.lat,
      Long: this.long,
      Brand: value.Brand || '',
      Email: value.Email || '',
      Phone: value.Phone || '',
      City: value.City || '',
      PostalCode: value.PostalCode || '',
      Address: typeof value.Address === 'string'
          ? value.Address
          : (value.Address as any).description,
      LocationTypeId: value.LocationTypeId || 0,
      ServiceableAreaRadius: value.Radius || 0,
      StoreType: value.StoreType || 0,
      ImageUrl: value.ImageUrl,
      Id: 0,
    };

    this.storeService
      .createStore(store, this.messageService)
      .then((data) => {
        this.dialogRef.close({
          Name: value.Name,
          MerchantId: this.merchantId,
          Brand: value.Brand,
          Email: value.Email,
          Phone: value.Phone,
          City: value.City,
          PostalCode: value.PostalCode,
          AddressDetails: value.AddressDetails,
          Address: value.Address,
          LocationTypeId: value.LocationTypeId,
          ServiceableAreaRadius: value.Radius,
          StoreType: value.StoreType,
          ImageUrl: data.ImageUrl,
          Id: data.Id,
        });
      })
      .catch((err) => {
        this.dialogRef.close();
        throw err;
      })
      .finally(() => (this.isLoading = false));
  }

  get merchantId(): number {
    const reviewMerchantId = localStorage.getItem('reviewMerchantId');
    let merchantId = this.createStoreForm?.get('MerchantId')?.value;

    if (reviewMerchantId) {
      merchantId = JSON.parse(reviewMerchantId);
    }

    return merchantId ?? 0;
  }

  updateRadius(radius: number) {
    if (this.circle && radius > 0) {
      this.circle.setRadius(radius * 1000);
    }
  }

  // ? Google Maps API functions
  private setMaps(): void {
    this.loadGoogleMapsApi().then(() => {
      this.initAutocompleteService();
      this.initMap();
      this.initGeocoder();
      this.filteredOptions$ = this.createStoreForm
        .get('Address')
        ?.valueChanges.pipe(
          startWith(''),
          switchMap((value) => this.filter(value || ''))
        );
      this.geocodeAddress();
      this.setStreetAddressValidator();

      this.createStoreForm.get('Radius')?.setValue(this.editedStore ? this.editedStore.ServiceableAreaRadius : 5);

      if (this.circle) {
        this.circle.setMap(null);
      }

      this.circle = new window.google.maps.Circle({
        map: this.map,
        center: this.editedStore
          ? { lat: this.editedStore.Lat, lng: this.editedStore.Long }
          : { lat: 14.5995, lng: 120.9842 },
        radius: (this.createStoreForm.value.Radius ?? 5) * 1000,
        strokeColor: '#007CFF',
        strokeWeight: 1,
        fillColor: '#007CFF',
      });
    });
  }

  private loadGoogleMapsApi(): Promise<void> {
    return new Promise((resolve) => {
      if (window.google && window.google.maps) {
        resolve();
        return;
      }
    });
  }

  private initAutocompleteService(): void {
    this.autocompleteService =
      new window.google.maps.places.AutocompleteService();
    this.placesService = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );
  }

  private initMap(): void {
    this.map = new window.google.maps.Map(this.mapContainer.nativeElement, {
      zoom: 13,
      center: this.editedStore
          ? { lat: this.editedStore.Lat, lng: this.editedStore.Long }
          : { lat: 14.5995, lng: 120.9842 }
    });
  }

  private initGeocoder(): void {
    this.geocoder = new window.google.maps.Geocoder();
  }

  private geocodeAddress(): void {
    if (!this.editedStore) {
      return;
    }

    if (!this.geocoder) {
      console.error('Geocoder not initialized yet.');
      return;
    }

    this.geocoder.geocode(
      { address: this.editedStore.Address },
      (results, status) => {
        if (
          status === window.google.maps.GeocoderStatus.OK &&
          results &&
          results.length > 0
        ) {
          const location = results[0].geometry.location;
          this.map?.setCenter(location);
          this.map?.setZoom(13);
          this.currentMarker = new window.google.maps.Marker({
            map: this.map,
            position: location,
            title: this.editedStore.Address,
            draggable: true,
          });
          this.addMarkerListener(this.currentMarker);
        } else {
          console.error(
            'Geocode was not successful for the following reason:',
            status
          );
        }
      }
    );
  }

  private setStreetAddressValidator(): void {
    if (!this.data?.stores) {
      return;
    }

    this.createStoreForm
      .get('Address')
      ?.addValidators(duplicateAddressValidator(this.data.stores));
  }

  private filter(
    value: string
  ): Observable<google.maps.places.AutocompletePrediction[]> {
    if (!value) {
      return new Observable((observer) => observer.next([]));
    }

    return new Observable((observer) => {
      this.autocompleteService?.getPlacePredictions(
        { input: value, componentRestrictions: { country: 'PH' } },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            observer.next(predictions);
          } else {
            observer.next([]);
          }
        }
      );
    });
  }

  displayFn(place: google.maps.places.AutocompletePrediction | string): string {
    if (place) {
      return typeof place === 'string' ? place : place.description;
    }

    return '';
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedPlace = event.option.value;
    if (!this.validateDuplicateAddress(selectedPlace.description)) {
      this.createStoreForm.get('Address')?.setValue(selectedPlace);
    }

        this.placesService?.getDetails(
        { placeId: selectedPlace.place_id },
        (place, status) => {
          if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              place &&
              place.geometry &&
              place.geometry.location
          ) {
            this.map?.setCenter(place.geometry.location);
            this.map?.setZoom(13);

            this.lat = place.geometry.location.lat();
            this.long = place.geometry.location.lng();

            // Marker
            if (!this.currentMarker) {
              this.currentMarker = new window.google.maps.Marker({
                map: this.map,
                position: place.geometry.location,
                title: place.name,
                draggable: true,
              });
              this.addMarkerListener(this.currentMarker);
            } else {
              this.currentMarker.setPosition(place.geometry.location);
              this.currentMarker.setTitle(place.name);
            }

            if (this.circle) {
              this.circle.setMap(null);
            }

            this.circle = new window.google.maps.Circle({
              map: this.map,
              center: place.geometry.location,
              radius: (this.createStoreForm.value.Radius ?? 5) * 1000,
              strokeColor: '#007CFF',
              strokeWeight: 1,
              fillColor: '#007CFF',
            });
          }
        }
    );
  }

  private addMarkerListener(marker: google.maps.Marker): void {
    marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
      const newLatLng: google.maps.LatLng | null = event.latLng;
      if (!newLatLng) {
        return;
      }
      const newLat = newLatLng?.lat();
      const newLng = newLatLng?.lng();
      this.reverseGeocode(newLat, newLng);
    });
  }

  private reverseGeocode(lat: number, lng: number): void {
    if (!this.geocoder) {
      console.error('Geocoder not initialized yet.');
      return;
    }

    const latlng = { lat, lng };

    this.geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === window.google.maps.GeocoderStatus.OK) {
        if (results && results[0]) {
          const formattedAddress = results[0].formatted_address;
          this.createStoreForm.get('Address')?.setValue(formattedAddress);
          this.createStoreForm
            .get('Address')
            ?.setValue(formattedAddress);
        } else {
          console.warn('No results found for reverse geocoding.');
        }
      } else {
        console.error('Reverse geocoding failed:', status);
      }
    });
  }

  private validateDuplicateAddress(chosenAddress: string): boolean {
    if (!this.data?.stores || !this.data.stores.length) {
      return false;
    }

    const isDuplicate = this.data.stores.some(
      ({ Address }) => Address === chosenAddress
    );

    return isDuplicate;
  }
}
